"use client";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/inline-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAppStore, currentView } from "@/lib/store";
import { apiGet, apiPost } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n/provider";
import { Check, Loader2, X } from "lucide-react";

const USERNAME_RE = /^[a-zA-Z][a-zA-Z0-9_-]{2,29}$/;

type UsernameState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "available" }
  | { kind: "taken" }
  | { kind: "invalid"; message: string }
  | { kind: "tooShort" };

export function AuthModal() {
  const { authOpen, authMode, closeAuth, openAuth, setUser } = useAppStore();
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [regForm, setRegForm] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });
  const [usernameState, setUsernameState] = useState<UsernameState>({ kind: "idle" });
  const lastChecked = useRef("");

  // Reset form state when the modal opens/closes.
  useEffect(() => {
    if (!authOpen) {
      setUsernameState({ kind: "idle" });
      lastChecked.current = "";
    }
  }, [authOpen]);

  // Debounced async username availability check on key-up.
  useEffect(() => {
    const value = regForm.username.trim();
    if (!value) {
      setUsernameState({ kind: "idle" });
      lastChecked.current = "";
      return;
    }
    if (value.length < 3) {
      setUsernameState({ kind: "tooShort" });
      lastChecked.current = "";
      return;
    }
    if (!USERNAME_RE.test(value)) {
      setUsernameState({ kind: "invalid", message: t("username.invalid") });
      lastChecked.current = "";
      return;
    }
    // Skip duplicate checks for the same value (e.g. after a rapid re-render).
    if (lastChecked.current === value) return;
    let cancelled = false;
    setUsernameState({ kind: "checking" });
    const timer = setTimeout(async () => {
      try {
        const res = await apiGet<{ available: boolean; valid?: boolean }>(
          `/api/auth/check-username?username=${encodeURIComponent(value)}`
        );
        if (!cancelled) {
          setUsernameState(res.available ? { kind: "available" } : { kind: "taken" });
          lastChecked.current = value;
        }
      } catch {
        if (!cancelled) setUsernameState({ kind: "idle" });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [regForm.username, t]);

  const usernameBlocked =
    usernameState.kind === "taken" ||
    usernameState.kind === "invalid" ||
    usernameState.kind === "tooShort" ||
    usernameState.kind === "checking";

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiPost<{ user: any }>("/api/auth/login", loginForm);
      setUser(res.user);
      qc.clear();
      closeAuth();
      toast.success(t("toast.welcomeBack", { name: res.user.firstName }));
    } catch (err: any) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (usernameState.kind !== "available") {
      toast.error(t("username.taken"));
      return;
    }
    setBusy(true);
    try {
      const res = await apiPost<{ user: any }>("/api/auth/register", {
        ...regForm,
        phone: regForm.phone || null,
        preferredLang: locale,
      });
      setUser(res.user);
      qc.clear();
      closeAuth();
      toast.success(t("toast.accountCreated", { name: res.user.firstName }));
    } catch (err: any) {
      toast.error(err.message || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={(o) => (o ? null : closeAuth())}>
      <DialogContent className="mx-auto w-full max-w-md">
        <DialogHeader>
          <DialogTitle>{t("auth.welcome")}</DialogTitle>
          <DialogDescription>{t("auth.subtitle")}</DialogDescription>
        </DialogHeader>

        <Tabs
          value={authMode}
          onValueChange={(v) => openAuth(v as "login" | "register")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">{t("auth.tabSignin")}</TabsTrigger>
            <TabsTrigger value="register">{t("auth.tabRegister")}</TabsTrigger>
          </TabsList>

          {/* ---------------- Sign in ---------------- */}
          <TabsContent value="login">
            <form onSubmit={onLogin} className="grid gap-4 pt-2">
              <div className="grid gap-1.5">
                <Label htmlFor="login-username">{t("auth.field.username")}</Label>
                <Input
                  id="login-username"
                  required
                  autoComplete="username"
                  value={loginForm.username}
                  onChange={(e) =>
                    setLoginForm((f) => ({ ...f, username: e.target.value }))
                  }
                  placeholder={t("auth.field.usernamePlaceholder")}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="login-password">{t("auth.field.password")}</Label>
                <Input
                  id="login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={loginForm.password}
                  onChange={(e) =>
                    setLoginForm((f) => ({ ...f, password: e.target.value }))
                  }
                  placeholder={t("auth.field.passwordPlaceholder")}
                />
              </div>
              <Button type="submit" disabled={busy}>
                {busy ? t("auth.signingIn") : t("common.signin")}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                {t("auth.demoHint")}
              </p>
            </form>
          </TabsContent>

          {/* ---------------- Create account ---------------- */}
          <TabsContent value="register">
            <form onSubmit={onRegister} className="grid gap-4 pt-2">
              <div className="grid gap-1.5">
                <Label htmlFor="reg-username">{t("auth.field.username")}</Label>
                <div className="relative">
                  <Input
                    id="reg-username"
                    required
                    autoComplete="username"
                    value={regForm.username}
                    onChange={(e) =>
                      setRegForm((f) => ({ ...f, username: e.target.value }))
                    }
                    placeholder={t("auth.field.usernamePlaceholder")}
                    className={`pr-10 ${
                      usernameState.kind === "available"
                        ? "border-emerald-500"
                        : usernameState.kind === "taken" || usernameState.kind === "invalid"
                        ? "border-rose-500"
                        : ""
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameState.kind === "checking" && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {usernameState.kind === "available" && (
                      <Check className="h-4 w-4 text-emerald-600" />
                    )}
                    {(usernameState.kind === "taken" ||
                      usernameState.kind === "invalid" ||
                      usernameState.kind === "tooShort") && (
                      <X className="h-4 w-4 text-rose-500" />
                    )}
                  </div>
                </div>
                <UsernameFeedback state={usernameState} t={t} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="reg-first">{t("auth.field.firstName")}</Label>
                  <Input
                    id="reg-first"
                    required
                    value={regForm.firstName}
                    onChange={(e) =>
                      setRegForm((f) => ({ ...f, firstName: e.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="reg-last">{t("auth.field.lastName")}</Label>
                  <Input
                    id="reg-last"
                    required
                    value={regForm.lastName}
                    onChange={(e) =>
                      setRegForm((f) => ({ ...f, lastName: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="reg-email">{t("auth.field.email")}</Label>
                <Input
                  id="reg-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={regForm.email}
                  onChange={(e) =>
                    setRegForm((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder={t("auth.field.emailPlaceholder")}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="reg-phone">{t("auth.field.phone")}</Label>
                <Input
                  id="reg-phone"
                  autoComplete="tel"
                  value={regForm.phone}
                  onChange={(e) =>
                    setRegForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  placeholder={t("auth.field.phonePlaceholder")}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="reg-password">{t("auth.field.password")}</Label>
                <Input
                  id="reg-password"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={regForm.password}
                  onChange={(e) =>
                    setRegForm((f) => ({ ...f, password: e.target.value }))
                  }
                  placeholder={t("auth.field.passwordPlaceholder")}
                />
                <p className="text-xs text-muted-foreground">
                  {t("auth.field.passwordHint")}
                </p>
              </div>
              <Button type="submit" disabled={busy || usernameBlocked}>
                {busy ? t("auth.creating") : t("auth.createAccount")}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function UsernameFeedback({
  state,
  t,
}: {
  state: UsernameState;
  t: (k: string, p?: Record<string, string | number>) => string;
}) {
  let cls = "text-xs text-muted-foreground";
  let text = "";
  if (state.kind === "checking") {
    cls = "text-xs text-muted-foreground";
    text = t("username.checking");
  } else if (state.kind === "available") {
    cls = "text-xs text-emerald-600";
    text = t("username.available");
  } else if (state.kind === "taken") {
    cls = "text-xs text-rose-600";
    text = t("username.taken");
  } else if (state.kind === "invalid") {
    cls = "text-xs text-rose-600";
    text = state.message || t("username.invalid");
  } else if (state.kind === "tooShort") {
    cls = "text-xs text-muted-foreground";
    text = t("username.tooShort");
  } else {
    text = t("auth.field.usernameHint");
  }
  return <p className={cls}>{text}</p>;
}
